import { useCallback, useEffect, useRef } from 'react';
import { useIntl } from 'react-intl';
import { useNavigate } from 'react-router-dom';

import { useSaveAnalyticalDatasetMutation } from '../api/useSaveAnalyticalDatasetMutation';
import { getAnalyticalDatasetVersionPath } from '../routes';
import { useAdRouteParams } from './useAdRouteParams';
import { selectCohorts, selectVariables } from '../state/selectors';
import { useAnalyticalDatasetDefinitionStore } from '../state/useAnalyticalDatasetDefinitionStore';
import type { UseAdSaveModalArgs, UseAdSaveModalResult } from '../types';

export const useAdSaveModal = ({ onClose }: UseAdSaveModalArgs): UseAdSaveModalResult => {
  const { formatMessage } = useIntl();
  const navigate = useNavigate();
  const { projectId, analyticalDatasetProfileId, analyticalDatasetVersionId } = useAdRouteParams();

  const cohorts = useAnalyticalDatasetDefinitionStore(selectCohorts);
  const variables = useAnalyticalDatasetDefinitionStore(selectVariables);

  const { mutate, isPending, error, reset } = useSaveAnalyticalDatasetMutation();

  const runSave = useCallback(() => {
    if (!projectId || !analyticalDatasetProfileId || !analyticalDatasetVersionId) return;
    mutate(
      {
        projectId,
        analyticalDatasetProfileId,
        analyticalDatasetVersionId,
        cohorts,
        variables,
      },
      {
        onSuccess: (envelope) => {
          const newVersionId = envelope.data.analyticalDatasetVersionId;
          // `replace: true` so the back button doesn't return to the pre-save
          // URL (which holds the now-superseded version id).
          navigate(
            getAnalyticalDatasetVersionPath(projectId, analyticalDatasetProfileId, newVersionId),
            { replace: true }
          );
          onClose();
        },
      }
    );
  }, [
    projectId,
    analyticalDatasetProfileId,
    analyticalDatasetVersionId,
    cohorts,
    variables,
    mutate,
    navigate,
    onClose,
  ]);

  // Auto-fire on mount — the modal exists to block the UI while the save
  // round-trip runs. Empty deps so the call closes over the values that
  // existed when the modal opened; Retry handles re-fires explicitly. The ref
  // guard keeps the one-shot dispatch idempotent under React StrictMode's dev
  // double-invoke of mount effects (otherwise the PATCH fires twice).
  const hasFiredRef = useRef(false);
  useEffect(
    () => {
      if (hasFiredRef.current) return;
      hasFiredRef.current = true;
      runSave();
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  );

  const onRetry = useCallback(() => {
    reset();
    runSave();
  }, [reset, runSave]);

  return {
    title: formatMessage({ id: 'analyticalDatasetDetail.saveModal.title' }),
    loadingTitle: formatMessage({ id: 'analyticalDatasetDetail.saveModal.loadingTitle' }),
    loadingText: formatMessage({ id: 'analyticalDatasetDetail.saveModal.loadingText' }),
    errorTitle: formatMessage({ id: 'analyticalDatasetDetail.saveModal.errorTitle' }),
    errorText: formatMessage({ id: 'analyticalDatasetDetail.saveModal.errorText' }),
    retryButtonLabel: formatMessage({ id: 'analyticalDatasetDetail.saveModal.retryButton' }),
    isSubmitting: isPending,
    submittingError: error ?? null,
    onRetry,
  };
};
